import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Define the default icon
const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Export the shared status check function
export const getMarkerStatus = (sensor, lastReadings) => {
  const lastReading = lastReadings[sensor.id];
  if (!lastReading) return 'no-data';

  const timestamp = new Date(lastReading.timestamp);
  const now = new Date();
  const hoursDiff = (now - timestamp) / (1000 * 60 * 60);

  if (hoursDiff <= 1) return 'active';
  if (hoursDiff <= 3) return 'delayed';
  return 'inactive';
};

// Create colored marker icons
const createColoredIcon = (status) => {
  const markerHtmlStyles = `
    .marker-${status} {
      filter: ${
        status === 'delayed' ? 'hue-rotate(180deg)' :
        status === 'inactive' ? 'hue-rotate(158deg)' :
        status === 'no-data' ? 'saturate(0)' :
        'none'  // default blue for active
      };
    }
  `;
  
  const style = document.createElement('style');
  style.textContent = markerHtmlStyles;
  document.head.appendChild(style);

  return L.divIcon({
    html: `<img src="${icon}" class="marker-${status}" style="width: 25px; height: 41px;">`,
    className: '',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });
};

// Map controller component for handling view updates
function MapController({ selectedSensorCoords }) {
  const map = useMap();
  
  useEffect(() => {
    if (selectedSensorCoords) {
      map.setView(selectedSensorCoords, 18);
    }
  }, [selectedSensorCoords, map]);
  
  return null;
}

const SensorMap = ({ sensors, onSensorClick, selectedSensor, lastReadings }) => {
  const mapRef = useRef(null);
  const selectedSensorCoords = sensors.find(s => s.id === selectedSensor)?.coordinates;

  const handleFocusSensor = (coords) => {
    if (mapRef.current) {
      mapRef.current.setView(coords, 18);
    }
  };

  // Get location-specific map configuration
  const getMapConfig = () => {
    const site = sensors[0]?.site;
    switch (site) {
      case 'CSU':
        return { center: [41.71982, -87.61266], zoom: 17 };
      case 'NEIU':
        return { center: [41.97978, -87.71687], zoom: 16 };
      case 'NU':
        return { center: [42.05209, -87.67694], zoom: 17 };
      case 'UIC':
        return { center: [41.86958, -87.64685], zoom: 17 };
      default:
        return { 
          center: sensors[0]?.coordinates || [41.71982, -87.61266], 
          zoom: 17 
        };
    }
  };

  const mapConfig = getMapConfig();

  // Add CSS for legend markers
  useEffect(() => {
    const styles = `
      .legend-marker-active { filter: none; }
      .legend-marker-delayed { filter: hue-rotate(180deg); }
      .legend-marker-inactive { filter: hue-rotate(158deg); }
      .legend-marker-no-data { filter: saturate(0); }
    `;
    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);

    return () => styleSheet.remove();
  }, []);

  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold mb-3 text-green-700">Sensor Map</h2>
      <div className="border rounded-lg overflow-hidden shadow-md">
        <MapContainer 
          center={mapConfig.center}
          zoom={mapConfig.zoom}
          style={{ height: "400px" }}
          ref={mapRef}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
          />
          {sensors.map((sensor) => {
            const status = getMarkerStatus(sensor, lastReadings);
            const lastReading = lastReadings[sensor.id];
            
            return (
              <Marker 
                key={sensor.id} 
                position={sensor.coordinates}
                icon={createColoredIcon(status)}
              >
                <Popup>
                  <div className="text-center p-2">
                    <h3 className="text-lg font-semibold mb-1">{sensor.id}</h3>
                    <p className="text-sm mb-1">Species: {sensor.species}</p>
                    <p className="text-sm mb-1">Diameter: {sensor.diameter}</p>
                    <p className="text-sm mb-2">
                      Last reading: {lastReading 
                        ? `${lastReading.Date} ${lastReading.Time}`
                        : 'No recent data'
                      }
                    </p>
                    {lastReading && (
                      <div className="text-xs text-gray-600 mb-2">
                        {lastReading.uncorrected_inner && (
                          <p>Inner: {lastReading.uncorrected_inner.toFixed(2)} cm/hr</p>
                        )}
                        {lastReading.uncorrected_outer && (
                          <p>Outer: {lastReading.uncorrected_outer.toFixed(2)} cm/hr</p>
                        )}
                      </div>
                    )}
                    <button 
                      onClick={() => {
                        onSensorClick(sensor);
                        handleFocusSensor(sensor.coordinates);
                      }}
                      className="mt-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded text-sm transition-colors duration-200"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <MapController selectedSensorCoords={selectedSensorCoords} />
        </MapContainer>
      </div>
      
      {/* Legend */}
      <div className="mt-3 flex items-center justify-end text-sm">
        <span className="mr-4">Sensor Status:</span>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <img 
              src={icon} 
              className="legend-marker-active h-5 w-auto mr-1" 
              alt="Active" 
            />
            <span>Active</span>
          </div>
          <div className="flex items-center">
            <img 
              src={icon} 
              className="legend-marker-delayed h-5 w-auto mr-1" 
              alt="Delayed" 
            />
            <span>Delayed</span>
          </div>
          <div className="flex items-center">
            <img 
              src={icon} 
              className="legend-marker-inactive h-5 w-auto mr-1" 
              alt="Inactive" 
            />
            <span>Inactive</span>
          </div>
          <div className="flex items-center">
            <img 
              src={icon} 
              className="legend-marker-no-data h-5 w-auto mr-1" 
              alt="No Data" 
            />
            <span>No Data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensorMap;